import {gameOverRequest} from "../services/routeService";
import {difficultyEnum} from "./handeEnums";

/**
 * Formats a given number of seconds into a string representation in the format MM:SS.
 * @param seconds The number of seconds to format.
 * @returns A string representing the formatted time.
 * @example
 * formatTime(90); // Returns "01:30"
 */
export const formatTime: (seconds: number) => string = (seconds: number): string => {
    const minutes: number = Math.floor(seconds / 60);
    const remainingSeconds: number = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

/**
 * Handles the game over process by making an asynchronous request to a game over endpoint.
 * It should be called when the game concludes.
 * @returns A Promise that resolves once the game over request has completed.
 * @example
 * await handleGameOver(); // Sends a game over request.
 */
export const handleGameOver: () => Promise<void> = async (): Promise<void> => {
    await gameOverRequest();
};

/**
 * Animates the transition of a time value from a start to a target, updating the UI to reflect
 * the current time and optionally calling a completion callback once finished. The UI is updated
 * through a provided function that handles the visual display of the current time.
 * @param startSeconds The starting time in seconds from which the animation should begin.
 * @param startTime
 * @param targetSeconds The target time in seconds the animation should reach.
 * @param updateUIFunc A function that updates the UI with the current time. Accepts a string
 *                     argument which is the formatted current time.
 * @param completeFunc An optional callback function to be called upon completion of the animation.
 * @example
 * animateToTime(0, 2700, (displayValue) => console.log(displayValue), () => console.log("Animation complete"));
 */
export const animateToTime: (
    startSeconds: number,
    startTime: number,
    targetSeconds: number,
    updateUIFunc: (displayValue: string) => void,
    completeFunc?: () => void
) => void = (
    startSeconds: number,
    startTime: number,
    targetSeconds: number,
    updateUIFunc: (displayValue: string) => void,
    completeFunc?: () => void
): void => {
    let animationFrameId: number;
    const animationDuration: number = 750;

    const animateStep: () => void = (): void => {
        const now: number = Date.now();
        const elapsedTime: number = now - startTime;
        const animationProgress: number = elapsedTime / animationDuration;
        // Calculate the current second based on animation progress.
        let currentSecond: number = startSeconds + (targetSeconds - startSeconds) * animationProgress;
        currentSecond = Math[targetSeconds > startSeconds ? "min" : "max"](currentSecond, targetSeconds);

        const displayValue: string = formatTime(Math.floor(currentSecond)); // Assuming formatTime is defined elsewhere
        updateUIFunc(displayValue);

        // Continue the animation until the elapsed time is less than the animation duration
        // and the current second hasn’t yet reached the target.
        if (elapsedTime < animationDuration) {
            animationFrameId = requestAnimationFrame(animateStep);
        } else {
            if (completeFunc) completeFunc(); // Call completion callback if provided
            cancelAnimationFrame(animationFrameId);
        }
    };

    animateStep();
};

/**
 * Animates a countdown transition of a time value, decreasing by 2 minutes, updating the UI to reflect
 * the current time and optionally calling a completion callback once finished. The UI is updated
 * through a provided function that handles the visual display of the current time.
 * @param startSeconds The starting time in seconds from which the countdown should begin.
 * @param startTime The timestamp when the animation starts, usually obtained with Date.now().
 * @param targetSeconds
 * @param updateUIFunc A function that updates the UI with the current time. Accepts a string
 *                     argument which is the formatted current time.
 * @param completeFunc An optional callback function to be called upon completion of the countdown.
 * @example
 * animateCountdown(2700, Date.now(), (displayValue) => console.log(displayValue), () => console.log("Countdown complete"));
 */
export const animateCountdown: (
    startSeconds: number,
    startTime: number,
    targetSeconds: number,
    updateUIFunc: (displayValue: string) => void,
    completeFunc?: () => void
) => void = (
    startSeconds: number,
    startTime: number,
    targetSeconds: number,
    updateUIFunc: (displayValue: string) => void,
    completeFunc?: () => void
): void => {
    let animationFrameId: number;
    const animationDuration: number = 750; // Duration in milliseconds over which the countdown occurs

    const animateStep: () => void = () => {
        const now: number = Date.now();
        const elapsedTime: number = now - startTime;
        const animationProgress: number = Math.min(elapsedTime / animationDuration, 1); // Ensure progress doesn't exceed 1
        let currentSecond: number = startSeconds + (targetSeconds - startSeconds) * animationProgress;
        currentSecond = Math.max(currentSecond, targetSeconds); // Ensure the countdown does not go below the target

        updateUIFunc(formatTime(Math.floor(currentSecond)));

        if (elapsedTime < animationDuration) {
            animationFrameId = requestAnimationFrame(animateStep);
        } else {
            if (completeFunc) {
                completeFunc();
            }
            cancelAnimationFrame(animationFrameId);
        }
    };

    animateStep();
};


export function getDifficultyFromSession(): difficultyEnum | null {
    const difficulty: string | null = localStorage.getItem("difficulty");
    if (difficulty !== null && Object.values(difficultyEnum).includes(difficulty as difficultyEnum)) {
        return (Object.keys(difficultyEnum) as Array<keyof typeof difficultyEnum>)
            .find(key => difficultyEnum[key] === difficulty as difficultyEnum) as difficultyEnum;
    }
    return null;
}